/**
 * Avatar Selector Component
 *
 * Allows users to select from avatars organized by categories.
 */

import { useState } from 'react'
import { Check, Lock } from 'lucide-react'

import { cn } from '@/lib/utils'
import {
  avatarCategories,
  getAvatarsByCategory,
  getAvatarById,
  getDefaultAvatar,
  type Avatar,
  type AvatarCategory,
} from '@/config/avatars'
import { useTranslation } from '@/hooks/useTranslation'
import { t } from '@/i18n'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

interface AvatarSelectorProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentAvatarId: string | null | undefined
  onSelect: (avatarId: string) => void
  isLoading?: boolean
}

export function AvatarSelector({
  open,
  onOpenChange,
  currentAvatarId,
  onSelect,
  isLoading = false,
}: AvatarSelectorProps) {
  const { t } = useTranslation()
  const [selectedId, setSelectedId] = useState<string | null>(currentAvatarId || null)
  const [activeCategory, setActiveCategory] = useState('one-piece')

  const handleSelect = (avatar: Avatar) => {
    if (!avatar.isPremium) {
      setSelectedId(avatar.id)
    }
  }

  const handleConfirm = () => {
    if (selectedId) {
      onSelect(selectedId)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('users.selectAvatar')}</DialogTitle>
          <DialogDescription>
            {t('users.selectAvatarDescription')}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeCategory} onValueChange={setActiveCategory} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${avatarCategories.length}, 1fr)` }}>
            {avatarCategories.map((category) => (
              <TabsTrigger
                key={category.id}
                value={category.id}
                disabled={category.isPremium}
                className="gap-1"
              >
                {category.icon && <span>{category.icon}</span>}
                {category.name}
                {category.isPremium && <Lock className="h-3 w-3 ml-1" />}
              </TabsTrigger>
            ))}
          </TabsList>

          {avatarCategories.map((category) => (
            <TabsContent
              key={category.id}
              value={category.id}
              className="flex-1 overflow-y-auto overflow-x-hidden mt-4 p-1"
            >
              {category.isPremium ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Lock className="h-12 w-12 text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground font-medium">
                    {category.description}
                  </p>
                </div>
              ) : (
                <CategoryAvatars
                  category={category}
                  selectedId={selectedId}
                  onSelect={handleSelect}
                />
              )}
            </TabsContent>
          ))}
        </Tabs>

        <div className="flex gap-2 pt-4 border-t">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            className="flex-1"
            onClick={handleConfirm}
            disabled={!selectedId || isLoading}
          >
            {isLoading ? t('common.saving') : t('common.confirm')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Category Avatars Grid
 */
function CategoryAvatars({
  category,
  selectedId,
  onSelect,
}: {
  category: AvatarCategory
  selectedId: string | null
  onSelect: (avatar: Avatar) => void
}) {
  const categoryAvatars = getAvatarsByCategory(category.id)

  if (categoryAvatars.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-muted-foreground">{t('users.noAvatars')}</p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
      {categoryAvatars.map((avatar) => (
        <button
          key={avatar.id}
          type="button"
          onClick={() => onSelect(avatar)}
          disabled={avatar.isPremium}
          className={cn(
            'relative p-2 rounded-xl border-2 transition-colors focus:outline-none focus:ring-2 focus:ring-primary',
            selectedId === avatar.id
              ? 'border-primary bg-primary/10'
              : 'border-transparent bg-muted/50 hover:bg-muted',
            avatar.isPremium && 'opacity-50 cursor-not-allowed'
          )}
        >
          <div className="relative aspect-square">
            <img
              src={avatar.path}
              alt={avatar.name}
              className="w-full h-full object-contain rounded-lg"
            />
            {selectedId === avatar.id && (
              <div className="absolute -top-1 -right-1 bg-primary text-primary-foreground rounded-full p-1">
                <Check className="h-3 w-3" />
              </div>
            )}
            {avatar.isPremium && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
                <Lock className="h-6 w-6 text-white" />
              </div>
            )}
          </div>
          <p className="text-xs font-medium text-center mt-1 truncate">
            {avatar.name}
          </p>
        </button>
      ))}
    </div>
  )
}

/**
 * User Avatar Display Component - Professional Edition
 *
 * Features:
 * - Smooth hover animations
 * - Status indicators
 * - Loading states
 * - Touch-friendly interactions
 * - Dark mode support
 */

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
type AvatarStatus = 'online' | 'offline' | 'busy' | 'away'

interface UserAvatarProps {
  avatarId: string | null | undefined
  name: string
  size?: AvatarSize
  className?: string
  onClick?: () => void
  showDefault?: boolean
  status?: AvatarStatus
  showStatusRing?: boolean
  isLoading?: boolean
}

const sizeConfig: Record<AvatarSize, { container: string; text: string; status: string; ring: string }> = {
  xs: { container: 'h-6 w-6', text: 'text-[10px]', status: 'h-1.5 w-1.5', ring: 'ring-1' },
  sm: { container: 'h-8 w-8', text: 'text-xs', status: 'h-2 w-2', ring: 'ring-1' },
  md: { container: 'h-10 w-10', text: 'text-sm', status: 'h-2.5 w-2.5', ring: 'ring-2' },
  lg: { container: 'h-14 w-14', text: 'text-lg', status: 'h-3 w-3', ring: 'ring-2' },
  xl: { container: 'h-20 w-20', text: 'text-2xl', status: 'h-4 w-4', ring: 'ring-2' },
  '2xl': { container: 'h-28 w-28', text: 'text-3xl', status: 'h-5 w-5', ring: 'ring-3' },
}

const statusColors: Record<AvatarStatus, string> = {
  online: 'bg-emerald-500',
  offline: 'bg-gray-400',
  busy: 'bg-red-500',
  away: 'bg-amber-500',
}

export function UserAvatar({
  avatarId,
  name,
  size = 'md',
  className,
  onClick,
  showDefault = true,
  status,
  showStatusRing = false,
  isLoading = false,
}: UserAvatarProps) {
  const avatar = getAvatarById(avatarId) || (showDefault ? getDefaultAvatar() : undefined)
  const config = sizeConfig[size]

  const containerStyles = cn(
    // Base
    'relative rounded-full overflow-hidden shrink-0',
    'bg-gradient-to-br from-gray-100 to-gray-200',
    'dark:from-gray-700 dark:to-gray-800',
    // Border
    'ring-1 ring-black/5 dark:ring-white/10',
    // Size
    config.container,
    // Interactive
    onClick && [
      'cursor-pointer',
      'transition-all duration-200 ease-out',
      'hover:scale-105 hover:shadow-lg hover:ring-2 hover:ring-primary/50',
      'active:scale-95',
      // Touch feedback
      'touch-manipulation',
    ],
    // Status ring
    showStatusRing && status && [
      config.ring,
      status === 'online' && 'ring-emerald-500/50',
      status === 'busy' && 'ring-red-500/50',
      status === 'away' && 'ring-amber-500/50',
      status === 'offline' && 'ring-gray-400/50',
    ],
    className
  )

  const imageStyles = cn(
    'h-full w-full object-cover',
    'transition-transform duration-300',
    onClick && 'group-hover:scale-110',
    isLoading && 'animate-pulse opacity-50'
  )

  // Loading state
  if (isLoading) {
    return (
      <div className={cn(containerStyles, 'animate-pulse')}>
        <div className="h-full w-full bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-600 dark:to-gray-700" />
      </div>
    )
  }

  if (avatar) {
    return (
      <div className="relative inline-block group">
        <div
          className={containerStyles}
          onClick={onClick}
          role={onClick ? 'button' : undefined}
          tabIndex={onClick ? 0 : undefined}
          title={onClick ? 'Cambiar avatar' : name}
          onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
        >
          {/* Shimmer effect on hover */}
          {onClick && (
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500 ease-out z-10" />
          )}

          <img
            src={avatar.path}
            alt={name}
            className={imageStyles}
            draggable={false}
          />
        </div>

        {/* Status indicator */}
        {status && (
          <div
            className={cn(
              'absolute -bottom-0.5 -right-0.5',
              'rounded-full',
              'ring-2 ring-white dark:ring-gray-900',
              config.status,
              statusColors[status],
              status === 'online' && 'animate-pulse'
            )}
          />
        )}
      </div>
    )
  }

  // Initials fallback
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <div className="relative inline-block group">
      <div
        className={cn(
          containerStyles,
          'flex items-center justify-center font-semibold',
          'bg-gradient-to-br from-primary to-primary/80',
          'text-primary-foreground',
          config.text
        )}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        title={onClick ? 'Cambiar avatar' : name}
        onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
      >
        {initials || 'U'}
      </div>

      {/* Status indicator */}
      {status && (
        <div
          className={cn(
            'absolute -bottom-0.5 -right-0.5',
            'rounded-full',
            'ring-2 ring-white dark:ring-gray-900',
            config.status,
            statusColors[status],
            status === 'online' && 'animate-pulse'
          )}
        />
      )}
    </div>
  )
}

/**
 * Avatar Group - Shows multiple avatars stacked
 */
interface AvatarGroupProps {
  users: Array<{ avatarId?: string | null; name: string }>
  max?: number
  size?: AvatarSize
}

export function AvatarGroup({ users, max = 4, size = 'sm' }: AvatarGroupProps) {
  const visibleUsers = users.slice(0, max)
  const remainingCount = users.length - max

  return (
    <div className="flex -space-x-2">
      {visibleUsers.map((user, index) => (
        <UserAvatar
          key={index}
          avatarId={user.avatarId}
          name={user.name}
          size={size}
          className="ring-2 ring-white dark:ring-gray-900"
        />
      ))}
      {remainingCount > 0 && (
        <div
          className={cn(
            'flex items-center justify-center rounded-full',
            'bg-gray-200 dark:bg-gray-700',
            'text-gray-600 dark:text-gray-300',
            'ring-2 ring-white dark:ring-gray-900',
            'text-xs font-medium',
            sizeConfig[size].container
          )}
        >
          +{remainingCount}
        </div>
      )}
    </div>
  )
}
